import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle,
  MapPin,
  Search,
  Eye,
  Calculator,
  Handshake,
  Phone,
} from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import FAQ from "@/components/common/FAQ";
import LeadForm from "@/components/common/LeadForm";
import { cityConfig } from "@/config/cityConfig";
import { useMeta, useSchema } from "@/hooks/useMeta";

const buyerFaqs = [
  {
    question: "Wie finde ich die richtige Immobilie im Kreis Mettmann?",
    answer:
      "Zunächst definieren wir gemeinsam Ihre Anforderungen: Lage, Größe, Budget und Objekttyp. Mit Ihrem Suchprofil werden Sie informiert, sobald ein passendes Objekt verfügbar ist – oft noch bevor es öffentlich vermarktet wird.",
  },
  {
    question: "Welche Nebenkosten kommen beim Immobilienkauf in NRW hinzu?",
    answer:
      "Beim Kauf in NRW fallen folgende Nebenkosten an: Grunderwerbsteuer 6,5%, Notarkosten ca. 1,5–2%, Grundbuchkosten ca. 0,5%, ggf. Maklerprovision 3,57%. Insgesamt sollten Sie ca. 12–13% des Kaufpreises als Nebenkosten einkalkulieren.",
  },
  {
    question: "Wie läuft der Kaufprozess ab?",
    answer:
      "Der Kaufprozess umfasst: 1) Beratung & Suchprofil, 2) Objektsuche & Besichtigungen, 3) Objektbewertung & Verhandlung, 4) Finanzierungsklärung, 5) Notartermin & Beurkundung, 6) Eigentumsübergabe. Wir begleiten Sie durch jeden Schritt.",
  },
  {
    question: "Kann ich prüfen, ob der Kaufpreis angemessen ist?",
    answer:
      "Ja, absolut. Wir analysieren den Kaufpreis anhand aktueller Marktdaten für die jeweilige Stadt. So können Sie sicher sein, zum fairen Marktpreis zu kaufen – ohne zu überzahlen.",
  },
  {
    question: "Was ist ein Suchprofil und wie hilft es mir?",
    answer:
      "Mit Ihrem persönlichen Suchprofil (Lage, Größe, Budget, Objekttyp) werden Sie gezielt informiert, wenn ein passendes Objekt verfügbar wird. Viele unserer Angebote werden zuerst vorgemerkten Suchkunden angeboten.",
  },
  {
    question: "Kann ich als Erstkäufer professionelle Begleitung erhalten?",
    answer:
      "Gerade für Erstkäufer ist professionelle Begleitung besonders wertvoll. Wir erklären alle Schritte verständlich, prüfen Objekte auf Mängel, unterstützen bei der Finanzierungssuche (inkl. KfW-Förderung) und begleiten Sie bis zur Schlüsselübergabe.",
  },
];

const buyerProcess = [
  { num: "01", title: "Beratung", desc: "Persönliches Gespräch zur Klärung Ihrer Wünsche und Möglichkeiten" },
  { num: "02", title: "Suchprofil", desc: "Erstellung eines individuellen Suchprofils für gezielte Immobiliensuche" },
  { num: "03", title: "Besichtigung", desc: "Professionelle Begleitung bei Besichtigungen & Objektbewertung" },
  { num: "04", title: "Kauf & Übergabe", desc: "Unterstützung bei Verhandlung, Notartermin & Schlüsselübergabe" },
];

const schema = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Immobilie kaufen im Kreis Mettmann",
  provider: { "@type": "RealEstateAgent", name: "BRIMO Immobilien NRW" },
  areaServed: "Kreis Mettmann",
  description: "Professionelle Begleitung beim Immobilienkauf im Kreis Mettmann. Suchprofil, Besichtigungen und Kaufabwicklung.",
};

export default function BuyerPage() {
  useMeta(
    "Immobilie kaufen im Kreis Mettmann | BRIMO Immobilien",
    "Immobilie kaufen im Kreis Mettmann mit BRIMO Immobilien. Persönliche Beratung, Suchprofil-Service und lokale Marktkenntnis. Jetzt Beratung vereinbaren."
  );
  useSchema(schema);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#1a1a1a]">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-end pb-16" data-testid="buyer-hero">
        <div className="absolute inset-0">
          <img
            src={cityConfig.images.buyerHero}
            alt="Immobilie kaufen Kreis Mettmann"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-[#0d0d0d]/90" />
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#1a1a1a] to-transparent" />
        </div>
        <div className="relative z-10 container mx-auto px-4 md:px-6 max-w-7xl pt-28">
          <div className="h-0.5 w-12 bg-[#E61D38] mb-5" />
          <p className="text-gray-300 text-xs uppercase tracking-[0.3em] mb-4">
            Für Kaufinteressenten im Kreis Mettmann
          </p>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-black text-white uppercase tracking-tight leading-none mb-5"
            style={{ fontFamily: "Montserrat, sans-serif" }}
          >
            IMMOBILIE KAUFEN
            <br />
            <span className="text-[#E61D38]">IM KREIS METTMANN</span>
          </h1>
          <p className="text-gray-300 text-lg max-w-2xl leading-relaxed mb-8">
            Wir begleiten Kaufinteressenten bei der Suche, Bewertung und dem Erwerb von Immobilien im
            Kreis Mettmann – persönlich, kompetent und diskret.
          </p>
          <div className="flex flex-wrap gap-4">
            <a
              href="#buyer-form"
              className="flex items-center gap-2 bg-[#E61D38] text-white px-7 py-4 font-bold uppercase tracking-wider text-sm hover:bg-[#c41930] transition-colors"
              data-testid="buyer-hero-cta"
            >
              Beratung vereinbaren
              <ArrowRight size={16} />
            </a>
            <a
              href={`tel:${cityConfig.phone}`}
              className="flex items-center gap-2 border border-white/30 text-white px-7 py-4 font-bold uppercase tracking-wider text-sm hover:border-white/60 transition-colors"
              data-testid="buyer-phone-cta"
            >
              <Phone size={14} />
              {cityConfig.phoneDisplay}
            </a>
          </div>
        </div>
      </section>

      {/* Buyer Intro */}
      <section className="py-16 md:py-24 bg-[#1a1a1a]" data-testid="buyer-intro">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="h-0.5 w-12 bg-[#E61D38] mb-6" />
          <h2
            className="text-3xl md:text-4xl font-black text-white uppercase tracking-tight mb-12"
            style={{ fontFamily: "Montserrat, sans-serif" }}
          >
            IHRE FRAGEN ALS KÄUFER
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: <Search className="text-[#E61D38]" size={28} />,
                q: "Welche Immobilien sind verfügbar?",
                a: "Mit unserem Netzwerk haben wir Zugang zu Angeboten – auch solchen, die noch nicht öffentlich vermarktet werden.",
              },
              {
                icon: <MapPin className="text-[#E61D38]" size={28} />,
                q: "Welche Lage passt zu mir?",
                a: "Hilden, Ratingen, Langenfeld, Haan oder Erkrath – wir kennen die Vor- und Nachteile jeder Stadt für Sie.",
              },
              {
                icon: <Handshake className="text-[#E61D38]" size={28} />,
                q: "Wie läuft der Kaufprozess ab?",
                a: "Vom Suchprofil bis zur Schlüsselübergabe – wir begleiten Sie Schritt für Schritt durch den gesamten Kaufprozess.",
              },
            ].map((item, i) => (
              <div
                key={i}
                className="bg-[#262626] border border-white/5 p-8 hover:border-[#E61D38]/30 transition-colors"
                data-testid={`buyer-question-${i}`}
              >
                <div className="mb-4">{item.icon}</div>
                <h3 className="text-white font-bold text-base mb-3">{item.q}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 md:py-24 bg-[#0d0d0d]" data-testid="buyer-services">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="h-0.5 w-12 bg-[#E61D38] mb-6" />
          <h2
            className="text-3xl md:text-4xl font-black text-white uppercase tracking-tight mb-12"
            style={{ fontFamily: "Montserrat, sans-serif" }}
          >
            UNSER SERVICE FÜR KÄUFER
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
            {[
              {
                icon: <Handshake size={24} className="text-[#E61D38]" />,
                title: "Käuferberatung",
                desc: "Persönliches Erstgespräch zur Klärung Ihrer Wünsche, Möglichkeiten und des Budgets.",
              },
              {
                icon: <Search size={24} className="text-[#E61D38]" />,
                title: "Suchprofil",
                desc: "Individuelles Suchprofil für gezielte Objektsuche – auch abseits der öffentlichen Angebote.",
              },
              {
                icon: <Calculator size={24} className="text-[#E61D38]" />,
                title: "Objektbewertung",
                desc: "Unabhängige Einschätzung des Kaufpreises anhand aktueller Marktdaten.",
              },
              {
                icon: <Eye size={24} className="text-[#E61D38]" />,
                title: "Kaufbegleitung",
                desc: "Professionelle Unterstützung bei Besichtigungen, Verhandlung und Notartermin.",
              },
            ].map((item, i) => (
              <div
                key={i}
                className="bg-[#262626] border border-white/5 p-7 hover:border-[#E61D38]/40 transition-all duration-300 group"
                data-testid={`buyer-service-${i}`}
              >
                <div className="mb-4 group-hover:scale-110 transition-transform origin-left">{item.icon}</div>
                <h3
                  className="text-white font-bold text-sm uppercase tracking-wider mb-3"
                  style={{ fontFamily: "Montserrat, sans-serif" }}
                >
                  {item.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Districts for Buyers */}
      <section className="py-16 md:py-24 bg-[#1a1a1a]" data-testid="buyer-districts">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="h-0.5 w-12 bg-[#E61D38] mb-6" />
          <h2
            className="text-3xl md:text-4xl font-black text-white uppercase tracking-tight mb-4"
            style={{ fontFamily: "Montserrat, sans-serif" }}
          >
            WOHNEN IM KREIS METTMANN
          </h2>
          <p className="text-gray-400 text-sm leading-relaxed mb-10 max-w-2xl">
            Der Kreis Mettmann bietet für jeden Lebensstil die passende Stadt – von lebhaften urbanen
            Lagen bis zu ruhigen Wohngebieten im Grünen.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              ...cityConfig.districts,
              { name: "Haus kaufen", slug: null, description: "Suchen Sie gezielt nach einem Haus im Kreis Mettmann.", link: "/haus-kaufen-kreis-mettmann" },
            ]
              .filter((d) => d.slug || d.link)
              .slice(0, 5)
              .map((district, i) => (
                <Link
                  key={i}
                  to={district.slug ? `/immobilie-verkaufen-${district.slug}` : district.link}
                  className="group bg-[#262626] border border-white/5 p-6 hover:border-[#E61D38]/50 transition-all duration-300"
                  data-testid={`buyer-district-${i}`}
                >
                  <div className="flex items-start gap-3 mb-3">
                    <MapPin size={16} className="text-[#E61D38] flex-shrink-0 mt-0.5" />
                    <h3 className="text-white font-bold text-sm uppercase tracking-wider">
                      {district.name}
                    </h3>
                  </div>
                  <p className="text-gray-500 text-xs leading-relaxed pl-7">{district.description}</p>
                  <div className="mt-4 pl-7 flex items-center gap-1 text-[#E61D38] text-xs font-bold uppercase tracking-wider">
                    <span>Mehr erfahren</span>
                    <ArrowRight size={10} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </section>

      {/* Buying Process */}
      <section className="py-16 md:py-24 bg-[#0d0d0d]" data-testid="buyer-process">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="h-0.5 w-12 bg-[#E61D38] mb-6" />
          <h2
            className="text-3xl md:text-4xl font-black text-white uppercase tracking-tight mb-12"
            style={{ fontFamily: "Montserrat, sans-serif" }}
          >
            DER KAUFPROZESS
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
            {buyerProcess.map((step, i) => (
              <div key={i} data-testid={`buyer-step-${i}`}>
                <div
                  className="text-[#E61D38] font-black text-3xl mb-3"
                  style={{ fontFamily: "Montserrat, sans-serif" }}
                >
                  {step.num}
                </div>
                <div className="h-0.5 w-full bg-[#E61D38]/20 mb-4" />
                <h3 className="text-white font-bold text-sm uppercase tracking-wider mb-2">
                  {step.title}
                </h3>
                <p className="text-gray-500 text-xs leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="py-16 md:py-20 bg-[#1a1a1a]" data-testid="buyer-trust">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="h-0.5 w-12 bg-[#E61D38] mb-6" />
              <h2
                className="text-2xl md:text-3xl font-black text-white uppercase tracking-tight mb-5"
                style={{ fontFamily: "Montserrat, sans-serif" }}
              >
                IHR PARTNER BEIM IMMOBILIENKAUF
              </h2>
              <p className="text-gray-400 text-sm leading-relaxed mb-6">
                Als Käufer profitieren Sie von unserem umfangreichen Marktwissen, unserem Netzwerk
                und unserer persönlichen Betreuung – von der ersten Anfrage bis zur
                Schlüsselübergabe.
              </p>
              <ul className="space-y-3">
                {[
                  "Unabhängige Objektbewertung",
                  "Zugang zu nicht öffentlichen Angeboten",
                  "Unterstützung bei Finanzierungsfragen",
                  "Begleitung beim Notartermin",
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-300 text-sm">
                    <CheckCircle size={14} className="text-[#E61D38] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <img
                src={cityConfig.manager.photo}
                alt={`${cityConfig.manager.name} – BRIMO Immobilien`}
                className="w-full max-w-xs mx-auto h-auto object-cover"
                loading="lazy"
              />
              <div className="absolute bottom-4 left-0 right-0 mx-4 bg-[#0d0d0d]/90 p-4">
                <p className="text-white font-bold text-sm">{cityConfig.manager.name}</p>
                <p className="text-[#E61D38] text-xs uppercase tracking-wider mt-0.5">
                  {cityConfig.manager.title}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FAQ items={buyerFaqs} title="FAQ – Immobilie kaufen im Kreis Mettmann" />

      {/* Lead Form */}
      <section id="buyer-form" className="py-16 md:py-24 bg-[#0d0d0d]" data-testid="buyer-form-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <div className="h-0.5 w-12 bg-[#E61D38] mb-6" />
              <h2
                className="text-3xl md:text-4xl font-black text-white uppercase tracking-tight mb-6"
                style={{ fontFamily: "Montserrat, sans-serif" }}
              >
                SUCHPROFIL ERSTELLEN
              </h2>
              <p className="text-gray-400 text-sm leading-relaxed mb-8">
                Erstellen Sie Ihr persönliches Suchprofil und werden Sie als Erstes informiert, wenn
                eine passende Immobilie im Kreis Mettmann verfügbar wird.
              </p>
              <div className="space-y-4">
                {[
                  "Bevorzugte Stadtteilempfehlung",
                  "Zugang zu Off-Market Angeboten",
                  "Persönliche Beratung inklusive",
                  "Schnelle Reaktion auf neue Objekte",
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle size={16} className="text-[#E61D38] flex-shrink-0" />
                    <span className="text-gray-300 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <LeadForm
                formType="buyer"
                title="Ihr Suchprofil"
                ctaText="Suchprofil senden"
                showDistrict={true}
                showBudget={true}
                showPropertyType={true}
                pageSource="/immobilie-kaufen-kreis-mettmann"
              />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
